import { graphql, useMutation } from 'utils/graphClient';
import {
  UploadContext,
  useUppyAwsS3UploadParametersMutation as useUppyAwsS3UploadParametersMutationType,
} from './__generated__/useUppyAwsS3UploadParametersMutation.graphql';
import Uppy, { UppyFile } from '@uppy/core';
export type { UploadContext } from './__generated__/useUppyAwsS3UploadParametersMutation.graphql';

export const PresignUploadMutation = graphql`
  mutation useUppyAwsS3UploadParametersMutation(
    $uploadContext: UploadContext!
    $fileName: String!
    $fileType: String!
    $fileSize: BigInt!
  ) {
    presignUpload(
      input: {
        uploadContext: $uploadContext
        fileName: $fileName
        fileType: $fileType
        fileSize: $fileSize
      }
    ) {
      uploadSignature {
        uploadToken
        signature {
          method
          url
          fields
          headers
        }
      }
    }
  }
`;

export const usePresignUploadMutation = () =>
  useMutation<useUppyAwsS3UploadParametersMutationType>(PresignUploadMutation);

// We create copy of type in aws-s3 uppy plugin, as it is not exposed.
interface AwsS3UploadParametersCopy {
  method: 'PUT';
  url: string;
  fields: Record<string, never>;
  headers: Record<string, string>;
}

const useUppyAwsS3UploadParameters = () => {
  const [presignUpload] = usePresignUploadMutation();

  const buildGetUploadParameters =
    ({ uppy, uploadContext }: { uppy: Uppy; uploadContext: UploadContext }) =>
    (file: UppyFile) => {
      return new Promise<AwsS3UploadParametersCopy>((resolve, reject) => {
        presignUpload({
          variables: {
            fileName: file.name,
            fileType: file.type || '',
            fileSize: file.size,
            uploadContext: uploadContext,
          },
          onCompleted: (data) => {
            if (data.presignUpload) {
              uppy.setFileMeta(file.id, {
                uploadToken: data.presignUpload.uploadSignature.uploadToken,
              });
              const { url, method, fields, headers } =
                data.presignUpload.uploadSignature.signature;
              resolve({
                url,
                method: method as 'PUT',
                fields: fields as Record<string, never>,
                headers: headers as Record<string, string>,
              });
            } else {
              reject(new Error('Something went wrong while uploading a file'));
            }
          },
        });
      });
    };

  return buildGetUploadParameters;
};

export default useUppyAwsS3UploadParameters;
